/**
 * COMPONENT: GuestSelectorPopup.jsx
 * Generic guest selector for reservations with text filtering.
 */

import React, { useCallback, useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "./PmsThemeContext";
import { useGuestProfileLanguage, getGuestCountryLabel } from "../reservations/guestProfileI18n";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1050,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  background: "rgba(2, 6, 23, 0.76)",
  backdropFilter: "blur(8px)",
};

const shellStyle = {
  width: "min(1100px, 96vw)",
  maxHeight: "90vh",
  overflow: "hidden",
  borderRadius: "24px",
  border: "1px solid rgba(148, 163, 184, 0.24)",
  background:
    "linear-gradient(180deg, rgba(8, 15, 30, 0.97), rgba(15, 23, 42, 0.96)) padding-box, linear-gradient(170deg, rgba(14, 165, 233, 0.35), rgba(59, 130, 246, 0.18)) border-box",
  boxShadow: "0 32px 80px rgba(2, 6, 23, 0.68)",
  color: "#e2e8f0",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  padding: "24px 24px 18px",
};

const eyebrowStyle = {
  margin: 0,
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(186, 230, 253, 0.84)",
};

const titleStyle = {
  margin: "4px 0 0",
  fontSize: "1.25rem",
  fontWeight: 800,
  color: "#f8fafc",
};

const subtitleStyle = {
  margin: "8px 0 0",
  fontSize: "0.88rem",
  lineHeight: 1.5,
  color: "rgba(226, 232, 240, 0.68)",
};

const closeButtonStyle = {
  width: "42px",
  height: "42px",
  borderRadius: "12px",
  border: "1px solid rgba(148, 163, 184, 0.32)",
  background: "rgba(15, 23, 42, 0.72)",
  color: "#e2e8f0",
  fontSize: "1.1rem",
  lineHeight: 1,
  cursor: "pointer",
  flex: "0 0 auto",
  transition: "transform 140ms ease, border-color 140ms ease, background 140ms ease",
};

const toolbarStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: "14px",
  alignItems: "center",
  padding: "0 24px 18px",
};

const searchInputStyle = {
  width: "100%",
  borderRadius: "14px",
  border: "1px solid rgba(148, 163, 184, 0.34)",
  background: "rgba(15, 23, 42, 0.82)",
  color: "#e2e8f0",
  padding: "12px 14px",
  fontSize: "0.95rem",
  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
};

const countBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "84px",
  padding: "10px 14px",
  borderRadius: "999px",
  border: "1px solid rgba(56, 189, 248, 0.24)",
  background: "rgba(14, 165, 233, 0.12)",
  color: "#bae6fd",
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.03em",
};

const tableWrapperStyle = {
  margin: "0 24px",
  borderRadius: "18px",
  border: "1px solid rgba(148, 163, 184, 0.22)",
  background: "rgba(2, 6, 23, 0.52)",
  overflow: "hidden",
  minHeight: "280px",
  maxHeight: "56vh",
  overflowY: "auto",
};

const tableStyle = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const tableHeaderCellStyle = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "rgba(15, 23, 42, 0.98)",
  color: "#bae6fd",
  fontSize: "0.76rem",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  padding: "14px 16px",
  borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
};

const tableCellStyle = {
  padding: "14px 16px",
  borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
  fontSize: "0.92rem",
  color: "#e2e8f0",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

const selectButtonStyle = {
  borderRadius: "10px",
  border: "1px solid rgba(56, 189, 248, 0.82)",
  background: "linear-gradient(135deg, rgba(14, 165, 233, 0.84), rgba(59, 130, 246, 0.92))",
  color: "#eff6ff",
  padding: "8px 12px",
  fontSize: "0.8rem",
  fontWeight: 800,
  letterSpacing: "0.03em",
  cursor: "pointer",
  width: "100%",
  transition: "transform 140ms ease, box-shadow 140ms ease",
};

const emptyStateStyle = {
  padding: "38px 24px",
  textAlign: "center",
  color: "rgba(226, 232, 240, 0.7)",
};

const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  padding: "18px 24px 24px",
  flexWrap: "wrap",
};

const footerTextStyle = {
  margin: 0,
  fontSize: "0.84rem",
  color: "rgba(226, 232, 240, 0.62)",
};

const cancelButtonStyle = {
  borderRadius: "12px",
  border: "1px solid rgba(248, 113, 113, 0.72)",
  background: "rgba(127, 29, 29, 0.18)",
  color: "#fecaca",
  padding: "11px 18px",
  fontSize: "0.9rem",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  transition: "transform 140ms ease, border-color 140ms ease, background 140ms ease",
};

const lightThemeStyles = {
  overlay: {
    background: "rgba(23, 38, 83, 0.42)",
  },
  shell: {
    border: "1px solid #d7e2ec",
    borderTop: "4px solid #3db6e9",
    borderRadius: "16px",
    background: "#ffffff",
    boxShadow: "0 24px 58px rgba(34, 58, 122, 0.2)",
    color: "#26354f",
  },
  eyebrow: {
    color: "#2b728e",
  },
  title: {
    color: "#172653",
  },
  subtitle: {
    color: "#5b6680",
  },
  closeButton: {
    border: "1px solid #bdcad8",
    background: "#ffffff",
    color: "#223a7a",
  },
  toolbar: {
    padding: "14px 24px",
    borderTop: "1px solid #e7edf3",
    borderBottom: "1px solid #d7e2ec",
    background: "#f7fafc",
  },
  searchInput: {
    border: "1px solid #bdcad8",
    background: "#ffffff",
    color: "#26354f",
    boxShadow: "none",
  },
  countBadge: {
    border: "1px solid #add7e5",
    background: "#edf8fc",
    color: "#223a7a",
  },
  tableWrapper: {
    margin: "16px 24px 0",
    border: "1px solid #d7e2ec",
    background: "#ffffff",
  },
  tableHeaderCell: {
    borderBottom: "3px solid #3db6e9",
    background: "#223a7a",
    color: "#ffffff",
  },
  tableCell: {
    borderBottom: "1px solid #d7e2ec",
    color: "#26354f",
  },
  selectButton: {
    border: "1px solid #223a7a",
    background: "#223a7a",
    color: "#ffffff",
  },
  emptyState: {
    color: "#5b6680",
  },
  footer: {
    borderTop: "1px solid #d7e2ec",
    background: "#ffffff",
  },
  footerText: {
    color: "#5b6680",
  },
  cancelButton: {
    border: "1px solid #d99ba5",
    background: "#ffffff",
    color: "#9b3445",
  },
};

const GuestSelectorPopup = ({
  show,
  onClose,
  reservationId,
  businessUnitId,
  onCustomerSelected,
  title = "Select Guest",
  subtitle = "Search and select any guest by name, email, country, phone or document.",
}) => {
  const { themeId } = usePmsTheme();
  const { languageId, t } = useGuestProfileLanguage();
  const isLightTheme = themeId === "light";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/customers", {
        params: businessUnitId ? { business_unit_id: businessUnitId } : undefined,
      });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("[GuestSelectorPopup] Error fetching customers:", err);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId]);

  useEffect(() => {
    if (!show) {
      setSearchTerm("");
      return;
    }

    setSearchTerm("");
    fetchCustomers();
  }, [show, fetchCustomers]);

  const handleSelect = async (customerId) => {
    try {
      const { data: customer } = await axios.get(`/api/customers/${customerId}`, {
        params: businessUnitId ? { business_unit_id: businessUnitId } : undefined,
      });

      if (onCustomerSelected) await onCustomerSelected(customer, reservationId);
      onClose();
    } catch (err) {
      console.error("[GuestSelectorPopup] Error selecting customer:", err);
      alert(t("Failed to select customer."));
    }
  };

  const query = searchTerm.trim().toLowerCase();
  const filteredCustomers = customers.filter((customer) => {
    if (!query) return true;

    const searchable = [
      customer?.first_name,
      customer?.middle_name,
      customer?.last_name,
      customer?.second_last_name,
      customer?.email,
      customer?.country,
      getGuestCountryLabel(customer?.country, languageId),
      customer?.city,
      customer?.phone,
      customer?.document_number,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchable.includes(query);
  });

  const rowCountLabel = loading
    ? t("Loading")
    : t(filteredCustomers.length === 1 ? t("{count} result") : t("{count} results"), { count: filteredCustomers.length });

  const themedStyle = (baseStyle, lightStyleKey) =>
    isLightTheme ? { ...baseStyle, ...lightThemeStyles[lightStyleKey] } : baseStyle;

  if (!show) return null;

  return (
    <div
      className={`guest-selector-overlay${isLightTheme ? " guest-selector-overlay--light" : ""}`}
      style={themedStyle(overlayStyle, "overlay")}
    >
      <style>{`
        .guest-selector-popup table tbody tr {
          transition: background 140ms ease;
        }

        .guest-selector-popup table tbody tr:hover {
          background: rgba(30, 41, 59, 0.66);
        }

        .guest-selector-popup button:hover {
          transform: translateY(-1px);
        }

        .guest-selector-popup input::placeholder {
          color: rgba(203, 213, 225, 0.56);
        }

        .guest-selector-popup.guest-selector-popup--light,
        body.rgv-brand-scope--light .guest-selector-popup.guest-selector-popup--light {
          width: min(980px, calc(100vw - 32px)) !important;
          max-height: calc(100dvh - 32px) !important;
          overflow: hidden !important;
          padding: 0 !important;
        }

        .guest-selector-popup--light table tbody tr:nth-child(even) td {
          background: #f7fafc;
        }

        .guest-selector-popup--light table tbody tr:hover td {
          background: #edf8fc;
        }

        .guest-selector-popup--light input::placeholder {
          color: #66738a;
          opacity: 1;
        }

        .guest-selector-popup--light input:focus-visible {
          outline: 3px solid rgba(61, 182, 233, 0.22);
          border-color: #3db6e9 !important;
        }

        .guest-selector-popup--light button:focus-visible {
          outline: 3px solid rgba(61, 182, 233, 0.28);
          outline-offset: 2px;
        }

        .guest-selector-popup--light .guest-selector-select-button:hover {
          background: #172653 !important;
          border-color: #172653 !important;
          box-shadow: 0 8px 18px rgba(34, 58, 122, 0.2);
        }

        .guest-selector-popup--light .guest-selector-cancel-button:hover {
          background: #fdeff1 !important;
          border-color: #9b3445 !important;
        }

        .guest-selector-popup--light .guest-selector-close-button:hover {
          background: #edf8fc !important;
          border-color: #3db6e9 !important;
        }

        @media (max-width: 768px) {
          .guest-selector-popup-toolbar {
            grid-template-columns: 1fr !important;
          }

          .guest-selector-popup.guest-selector-popup--light,
          body.rgv-brand-scope--light .guest-selector-popup.guest-selector-popup--light {
            width: calc(100vw - 20px) !important;
            max-height: calc(100dvh - 20px) !important;
          }

          .guest-selector-popup table {
            min-width: 680px;
          }
        }
      `}</style>

      <div
        className={`guest-selector-popup${isLightTheme ? " guest-selector-popup--light" : ""}`}
        style={themedStyle(shellStyle, "shell")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-selector-title"
      >
        <div style={headerStyle}>
          <div>
            <p style={themedStyle(eyebrowStyle, "eyebrow")}>{t("Guest Directory")}</p>
            <h2 id="guest-selector-title" style={themedStyle(titleStyle, "title")}>
              {t(title)}
            </h2>
            <p style={themedStyle(subtitleStyle, "subtitle")}>{t(subtitle)}</p>
          </div>

          <button
            type="button"
            className="guest-selector-close-button"
            aria-label={t("Close")}
            style={themedStyle(closeButtonStyle, "closeButton")}
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div
          className="guest-selector-popup-toolbar"
          style={themedStyle(toolbarStyle, "toolbar")}
        >
          <input
            type="text"
            aria-label={t("Search guests")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("Search by name, email, country, phone or document")}
            style={themedStyle(searchInputStyle, "searchInput")}
          />
          <div style={themedStyle(countBadgeStyle, "countBadge")}>{rowCountLabel}</div>
        </div>

        <div
          style={{
            ...themedStyle(tableWrapperStyle, "tableWrapper"),
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "none",
            overflow: "auto",
          }}
        >
          {loading ? (
            <div style={themedStyle(emptyStateStyle, "emptyState")}>{t("Loading guests...")}</div>
          ) : filteredCustomers.length === 0 ? (
            <div style={themedStyle(emptyStateStyle, "emptyState")}>
              {query ? t("No guests match your search.") : t("No guests found.")}
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th
                    style={{
                      ...themedStyle(tableHeaderCellStyle, "tableHeaderCell"),
                      width: "28%",
                    }}
                  >
                    {t("Name")}
                  </th>
                  <th
                    style={{
                      ...themedStyle(tableHeaderCellStyle, "tableHeaderCell"),
                      width: "26%",
                    }}
                  >
                    {t("Email")}
                  </th>
                  <th
                    style={{
                      ...themedStyle(tableHeaderCellStyle, "tableHeaderCell"),
                      width: "16%",
                    }}
                  >
                    {t("Country")}
                  </th>
                  <th
                    style={{
                      ...themedStyle(tableHeaderCellStyle, "tableHeaderCell"),
                      width: "16%",
                    }}
                  >
                    {t("Phone")}
                  </th>
                  <th
                    style={{
                      ...themedStyle(tableHeaderCellStyle, "tableHeaderCell"),
                      width: "14%",
                    }}
                  >
                    {t("Action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => {
                  const fullName = [
                    customer?.first_name,
                    customer?.middle_name,
                    customer?.last_name,
                    customer?.second_last_name,
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <tr key={customer.id}>
                      <td style={themedStyle(tableCellStyle, "tableCell")} title={fullName}>
                        {fullName || t("Unnamed guest")}
                      </td>
                      <td
                        style={themedStyle(tableCellStyle, "tableCell")}
                        title={customer?.email || ""}
                      >
                        {customer?.email || "-"}
                      </td>
                      <td
                        style={themedStyle(tableCellStyle, "tableCell")}
                        title={getGuestCountryLabel(customer?.country, languageId) || ""}
                      >
                        {getGuestCountryLabel(customer?.country, languageId) || "-"}
                      </td>
                      <td
                        style={themedStyle(tableCellStyle, "tableCell")}
                        title={customer?.phone || ""}
                      >
                        {customer?.phone || "-"}
                      </td>
                      <td style={themedStyle(tableCellStyle, "tableCell")}>
                        <button
                          type="button"
                          className="guest-selector-select-button"
                          style={themedStyle(selectButtonStyle, "selectButton")}
                          onClick={() => handleSelect(customer.id)}
                        >
                          {t("Select")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={themedStyle(footerStyle, "footer")}>
          <p style={themedStyle(footerTextStyle, "footerText")}>
            {t("Use the search bar to quickly filter the full guest list.")}
          </p>
          <button
            type="button"
            className="guest-selector-cancel-button"
            style={themedStyle(cancelButtonStyle, "cancelButton")}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestSelectorPopup;
